import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Snackbar,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { DataGrid } from '@mui/x-data-grid';
import Header from '../../components/Header';
import { tokens } from '../../theme';
import * as XLSX from 'xlsx';

const TYPES = [
  { value: 'additional_income', label: 'Additional Income' },
  { value: 'destination_expense', label: 'Destination Expenses' },
  { value: 'other_income', label: 'Other Income' },
  { value: 'other_expense', label: 'Other Expenses' },
];

const fmtMoney = (v) => Number(v || 0).toFixed(2);
const fmtEuro = (v) => `€ ${fmtMoney(v)}`;

/** Excel sheet names are limited to 31 characters; avoid invalid characters. */
const safeSheetName = (name, maxLen = 31) => {
  const cleaned = String(name || 'Sheet').replace(/[:\\/?*[\]]/g, '-');
  return cleaned.length > maxLen ? cleaned.slice(0, maxLen) : cleaned;
};

/** Currency as text for Excel cells (SheetJS stores display strings). */
const xlsAmount = (v) => {
  if (v === '' || v === null || v === undefined) return '';
  const n = Number(v);
  if (Number.isNaN(n)) return '';
  return fmtEuro(n);
};

/** Summary row: label in A, amount in D (matches coordinator export layout). */
const xlsSummaryRow = (label, amount) => [label, '', '', xlsAmount(amount)];

const AssignedTripDetails = () => {
  const { countryId, departureDate } = useParams();
  const token = localStorage.getItem('token');
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [toast, setToast] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [form, setForm] = useState({
    type: 'additional_income',
    name: '',
    amount: '',
    entryDate: departureDate,
  });

  const getData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_APILINK}/api/admin/assigned-trips/${countryId}/${departureDate}`, {
        headers: { token },
      });
      const data = await res.json();
      if (data.success) setReport(data.data);
      else setToast(data.message || 'Failed to load trip details');
    } catch (e) {
      setToast('Failed to load trip details');
    } finally {
      setLoading(false);
    }
  }, [countryId, departureDate, token]);

  useEffect(() => {
    getData();
  }, [getData]);

  const report1Columns = [
    { field: 'serialNo', headerName: 'Serial No', width: 100 },
    { field: 'destination', headerName: 'Destination', flex: 1, minWidth: 140 },
    { field: 'departureDate', headerName: 'Departure Date', flex: 1, minWidth: 130 },
    { field: 'endDate', headerName: 'End Date', flex: 1, minWidth: 120 },
    { field: 'guide', headerName: 'Guide', flex: 1, minWidth: 150 },
    { field: 'accommodation', headerName: 'Accommodation', flex: 1, minWidth: 160 },
    { field: 'busNo', headerName: 'Bus No', width: 100 },
    { field: 'excursionName', headerName: 'Excursion', flex: 1, minWidth: 150 },
    { field: 'persons', headerName: 'No of Persons', width: 130 },
  ];

  const report2Columns = [
    { field: 'serialNo', headerName: 'Serial No', width: 100 },
    { field: 'destination', headerName: 'Destination', flex: 1, minWidth: 140 },
    { field: 'departureDate', headerName: 'Departure Date', flex: 1, minWidth: 130 },
    { field: 'endDate', headerName: 'End Date', flex: 1, minWidth: 120 },
    { field: 'guide', headerName: 'Guide', flex: 1, minWidth: 150 },
    { field: 'busNo', headerName: 'Bus No', width: 100 },
    { field: 'excursionName', headerName: 'Excursion', flex: 1, minWidth: 170 },
    { field: 'passengers', headerName: 'Passengers', width: 120 },
    {
      field: 'totalCollected',
      headerName: 'Total amount (€)',
      width: 150,
      valueFormatter: ({ value }) => fmtEuro(value),
    },
    {
      field: 'guideFee',
      headerName: 'Guide Fee (€)',
      width: 130,
      valueFormatter: ({ value }) => fmtEuro(value),
    },
    {
      field: 'forSettlement',
      headerName: 'For Settlement (€)',
      width: 155,
      valueFormatter: ({ value }) => fmtEuro(value),
    },
  ];

  const financialRows = useMemo(() => {
    const actions = report?.coordinatorFunctionalActions ?? report?.financial;
    const guideActions = report?.guideFunctionalActions;
    if (!actions) return [];
    const items = [];
    for (const type of TYPES) {
      const arr = actions[type.value] || [];
      arr.forEach((row) => items.push({ ...row, type: type.value, typeLabel: type.label, source: 'coordinator' }));
      const guideArr = guideActions?.[type.value] || [];
      guideArr.forEach((row) => items.push({ ...row, type: type.value, typeLabel: type.label, source: 'guide' }));
    }
    return items;
  }, [report]);

  const settlementSummaryRows = useMemo(() => {
    const sum = report?.summary;
    if (!sum || typeof sum !== 'object') return [];
    return Object.entries(sum)
      .filter(([k]) => k !== 'Net settlement')
      .map(([label, value]) => ({
        label,
        value,
        negative: label === 'Gratis (€)' || label === 'Discount (€)' || label.startsWith('Guide fees'),
        dividerAfter:
          label === 'Discount (€)' || label === 'To be settled (€)' || label === 'Destination settlement',
        emphasize: label === 'Collected amount' || label === 'To be settled (€)' || label === 'Destination settlement',
      }));
  }, [report?.summary]);

  const netSettlementValue = report?.summary?.['Net settlement'];

  const financialColumns = [
    { field: 'typeLabel', headerName: 'Type', flex: 1, minWidth: 180 },
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 160 },
    {
      field: 'submittedBy',
      headerName: 'Submitted By',
      width: 150,
      valueGetter: ({ row }) => (row.source === 'guide' ? row.guideName || 'Guide' : 'Coordinator'),
    },
    { field: 'entryDate', headerName: 'Date', width: 120 },
    { field: 'amount', headerName: 'Amount (€)', width: 130, valueFormatter: ({ value }) => fmtEuro(value) },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 210,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        if (params.row.source === 'guide') {
          return (
            <Typography variant="body2" color={colors.grey[300]}>
              Read-only
            </Typography>
          );
        }
        return (
          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              size="small"
              sx={{ backgroundColor: colors.greenAccent[600], ':hover': { backgroundColor: colors.greenAccent[700] } }}
              onClick={() => {
                setEditing(params.row);
                setForm({
                  type: params.row.type,
                  name: params.row.name || '',
                  amount: String(params.row.amount ?? ''),
                  entryDate: params.row.entryDate || departureDate,
                });
                setDialogOpen(true);
              }}
            >
              Edit
            </Button>
            <Button
              variant="contained"
              size="small"
              sx={{ backgroundColor: colors.redAccent[500], ':hover': { backgroundColor: colors.redAccent[600] } }}
              onClick={() => {
                setRowToDelete(params.row);
                setDeleteConfirmOpen(true);
              }}
            >
              Delete
            </Button>
          </Box>
        );
      },
    },
  ];

  const saveFinancial = async () => {
    const payload = {
      type: form.type,
      name: form.name.trim(),
      amount: Number(form.amount || 0),
      entryDate: form.entryDate,
    };
    if (!payload.name) {
      setToast('Name is required');
      return;
    }
    const url = editing
      ? `${process.env.REACT_APP_APILINK}/api/admin/assigned-trips/financial-actions/${editing.id}`
      : `${process.env.REACT_APP_APILINK}/api/admin/assigned-trips/${countryId}/${departureDate}/financial-actions`;
    const method = editing ? 'PUT' : 'POST';
    const res = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json', token },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (data.success) {
      setDialogOpen(false);
      setEditing(null);
      setForm({ type: 'additional_income', name: '', amount: '', entryDate: departureDate });
      setToast(editing ? 'Updated' : 'Added');
      getData();
    } else {
      setToast(data.message || 'Save failed');
    }
  };

  const exportCombinedExcel = () => {
    if (!report) return;
    const wb = XLSX.utils.book_new();
    const dest = report.meta?.destination || '';
    const dep = report.meta?.departureDate || departureDate;

    const sheet1Rows = [
      ['Report 1 — Excursion Summary'],
      [`Destination: ${dest}`],
      [`Departure date: ${dep}`],
      [],
      ['Serial No', 'Destination', 'Departure Date', 'End Date', 'Guide', 'Accommodation', 'Bus No', 'Excursion', 'No of Persons'],
      ...(report.report1 || []).map((r) => [
        r.serialNo,
        r.destination,
        r.departureDate,
        r.endDate,
        r.guide,
        r.accommodation,
        r.busNo,
        r.excursionName,
        r.persons != null ? Number(r.persons) : '',
      ]),
    ];
    const ws1 = XLSX.utils.aoa_to_sheet(sheet1Rows);
    ws1['!cols'] = [{ wch: 8 }, { wch: 22 }, { wch: 14 }, { wch: 12 }, { wch: 22 }, { wch: 22 }, { wch: 8 }, { wch: 28 }, { wch: 12 }];
    XLSX.utils.book_append_sheet(wb, ws1, safeSheetName('Report 1'));

    const sum = report.summary || {};
    const sheet2Rows = [
      ['Report 2 — Destination Settlement'],
      [`Destination: ${dest}`],
      [`Departure date: ${dep}`],
      [],
      [
        'Serial No',
        'Destination',
        'Departure Date',
        'End Date',
        'Guide',
        'Bus No',
        'Excursion',
        'Passengers',
        'Total amount (€)',
        'Guide Fee (€)',
        'For Settlement (€)',
      ],
      ...(report.report2 || []).map((r) => [
        r.serialNo,
        r.destination,
        r.departureDate,
        r.endDate,
        r.guide,
        r.busNo,
        r.excursionName,
        r.passengers != null ? Number(r.passengers) : '',
        xlsAmount(r.totalCollected),
        xlsAmount(r.guideFee),
        xlsAmount(r.forSettlement),
      ]),
      [],
      [],
      ['Coordinator financial actions'],
      ['Type', 'Name', 'Date', 'Amount (€)'],
      ...financialRows
        .filter((row) => row.source !== 'guide')
        .map((row) => [row.typeLabel, row.name, row.entryDate, xlsAmount(row.amount)]),
      [],
      [],
      ['Guide financial actions'],
      ['Type', 'Name', 'Guide', 'Date', 'Amount (€)'],
      ...financialRows
        .filter((row) => row.source === 'guide')
        .map((row) => [row.typeLabel, row.name, row.guideName || '', row.entryDate, xlsAmount(row.amount)]),
      [],
      ['Summary (EUR)', '', '', ''],
      ...Object.entries(sum).flatMap(([label, val]) => {
        const rows = [];
        if (label === 'Net settlement') rows.push([]);
        rows.push(xlsSummaryRow(label, val ?? 0));
        if (label === 'Discount (€)' || label === 'To be settled (€)' || label === 'Destination settlement') rows.push([]);
        return rows;
      }),
    ];
    const ws2 = XLSX.utils.aoa_to_sheet(sheet2Rows);
    ws2['!cols'] = [
      { wch: 8 },
      { wch: 22 },
      { wch: 14 },
      { wch: 12 },
      { wch: 22 },
      { wch: 8 },
      { wch: 28 },
      { wch: 11 },
      { wch: 18 },
      { wch: 18 },
      { wch: 18 },
    ];
    XLSX.utils.book_append_sheet(wb, ws2, safeSheetName('Report 2 & Coordinator Fin Rep'));

    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([buf], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `assigned_trip_${countryId}_${departureDate}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
    setToast('Excel exported (2 sheets)');
  };

  const goBack = () => navigate('/assigned-trips');

  if (loading) {
    return (
      <Box m="20px">
        <Box display="flex" alignItems="center" mb={2}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={goBack}
            sx={{
              backgroundColor: colors.blueAccent[700],
              color: colors.grey[100],
              fontWeight: 700,
              ':hover': { backgroundColor: colors.blueAccent[800] },
            }}
          >
            Back to assigned trips
          </Button>
        </Box>
        <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
        <Box display="flex" alignItems="flex-start" gap={1} flex={1} minWidth={0}>
          <IconButton
            onClick={goBack}
            aria-label="Back to assigned trips"
            sx={{
              mt: 0.5,
              color: colors.grey[100],
              backgroundColor: colors.blueAccent[700],
              ':hover': { backgroundColor: colors.blueAccent[800] },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Header
            title="ASSIGNED TRIP DETAILS"
            subtitle={`${report?.meta?.destination || '-'} - ${report?.meta?.departureDate || departureDate}`}
          />
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={goBack}
            sx={{
              backgroundColor: colors.blueAccent[700],
              color: colors.grey[100],
              fontWeight: 700,
              ':hover': { backgroundColor: colors.blueAccent[800] },
            }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            onClick={exportCombinedExcel}
            disabled={!report}
            sx={{
              backgroundColor: colors.greenAccent[600],
              color: colors.grey[100],
              fontWeight: 700,
              ':hover': { backgroundColor: colors.greenAccent[700] },
            }}
          >
            Export REPORT
          </Button>
        </Box>
      </Box>

      <Card sx={{ mt: 2, backgroundColor: colors.primary[400] }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" gap={1}>
            <Typography variant="h4">Report 1 - Excursion Summary</Typography>
          </Box>
          <Box height="580px">
            <DataGrid
              rows={report?.report1 || []}
              columns={report1Columns}
              getRowId={(r) => `r1-${r.serialNo}`}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50, 100]}
              disableSelectionOnClick
            />
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mt: 2, backgroundColor: colors.primary[400] }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" gap={1}>
            <Typography variant="h4">Report 2 - Destination Settlement</Typography>
          </Box>
          <Box height="580px">
            <DataGrid
              rows={report?.report2 || []}
              columns={report2Columns}
              getRowId={(r) => `r2-${r.serialNo}`}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50, 100]}
              disableSelectionOnClick
            />
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mt: 2, backgroundColor: colors.primary[400] }}>
        <CardContent>
          <Typography variant="h4" mb={1}>
            Coordinator Financial Action
          </Typography>
          <Typography variant="body2" sx={{ color: colors.grey[300], mb: 2 }}>
            Add income and expense lines below, then review the summary totals (EUR) at the bottom.
          </Typography>
          <Box mb={1}>
            <Button
              variant="contained"
              sx={{ backgroundColor: colors.blueAccent[600], ':hover': { backgroundColor: colors.blueAccent[700] } }}
              onClick={() => {
                setEditing(null);
                setForm({ type: 'additional_income', name: '', amount: '', entryDate: departureDate });
                setDialogOpen(true);
              }}
            >
              Add Financial Action
            </Button>
          </Box>
          <Box height="580px" mb={3}>
            <DataGrid
              rows={financialRows}
              columns={financialColumns}
              getRowId={(r) => r.id}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50, 100]}
              disableSelectionOnClick
            />
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 700, color: colors.grey[100], mb: 1.5 }}>
            Summary
          </Typography>
          <Box
            sx={{
              p: 2,
              borderRadius: '10px',
              border: `1px solid ${colors.primary[500]}`,
              backgroundColor: colors.primary[500],
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {settlementSummaryRows.map((row, idx, arr) => (
              <Box
                key={row.label}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                gap={3}
                flexWrap="wrap"
                sx={{
                  minHeight: 28,
                  py: 0.125,
                  mb: row.dividerAfter ? 1.5 : idx < arr.length - 1 ? 0.75 : 0,
                  pb: row.dividerAfter ? 1.5 : 0,
                  borderBottom: row.dividerAfter ? `1px solid ${colors.primary[400]}` : 'none',
                }}
              >
                <Box>
                  <Typography
                    variant="body1"
                    sx={{
                      color: colors.grey[100],
                      fontWeight: row.emphasize ? 600 : 400,
                    }}
                  >
                    {row.label}
                  </Typography>
                </Box>
                <Typography
                  variant="body1"
                  sx={{
                    color: colors.grey[100],
                    fontWeight: row.emphasize ? 600 : 500,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {row.negative ? '- ' : ''}€ {fmtMoney(row.value)}
                </Typography>
              </Box>
            ))}
            <Box
              sx={{
                mt: 3.5,
                px: 2.5,
                py: 2,
                borderRadius: '12px',
                border: `2px solid ${colors.greenAccent[500]}`,
                backgroundColor: `${colors.greenAccent[700]}2a`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, color: colors.grey[100] }}>
                Net settlement
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  color: colors.greenAccent[400],
                  whiteSpace: 'nowrap',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                € {fmtMoney(netSettlementValue)}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Add/Edit Financial Action */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: colors.primary[400],
            backgroundImage: 'none',
            borderRadius: '16px',
            border: `1px solid ${colors.primary[500]}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: '1.15rem',
            color: colors.grey[100],
            borderBottom: `1px solid ${colors.primary[500]}`,
            pb: 1.5,
            px: 3,
          }}
        >
          {editing ? 'Edit' : 'Add'} Financial Action
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5, px: 3 }}>
          <Box
            sx={{
              mt: 0.5,
              mb: 2,
              px: 2,
              py: 1.5,
              borderRadius: '10px',
              border: `1px solid ${colors.blueAccent[700]}`,
              backgroundColor: colors.primary[500],
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: colors.grey[300],
                mb: 0.25,
                fontSize: '0.72rem',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              Trip Context
            </Typography>
            <Typography variant="body1" sx={{ color: colors.grey[100], fontWeight: 600 }}>
              {report?.meta?.destination || '-'} — {departureDate}
            </Typography>
          </Box>
          <TextField
            select
            fullWidth
            margin="normal"
            label="Type"
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value })}
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: '10px', backgroundColor: colors.primary[500] },
              '& .MuiInputLabel-root': { color: colors.grey[300] },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] },
            }}
          >
            {TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            margin="normal"
            label="Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: '10px', backgroundColor: colors.primary[500] },
              '& .MuiInputLabel-root': { color: colors.grey[300] },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] },
            }}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Amount (€)"
            type="number"
            inputProps={{ min: 0, step: 0.01 }}
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: '10px', backgroundColor: colors.primary[500] },
              '& .MuiInputLabel-root': { color: colors.grey[300] },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] },
            }}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={form.entryDate}
            onChange={(e) => setForm({ ...form, entryDate: e.target.value })}
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: '10px', backgroundColor: colors.primary[500] },
              '& .MuiInputLabel-root': { color: colors.grey[300] },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ borderTop: `1px solid ${colors.primary[500]}`, px: 3, py: 2, gap: 1.5 }}>
          <Button
            onClick={() => setDialogOpen(false)}
            variant="outlined"
            sx={{
              borderColor: colors.grey[500],
              color: colors.grey[200],
              ':hover': { borderColor: colors.grey[300], backgroundColor: `${colors.grey[700]}33` },
              borderRadius: '8px',
              px: 3,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveFinancial}
            sx={{
              backgroundColor: colors.greenAccent[600],
              color: '#fff',
              fontWeight: 700,
              ':hover': { backgroundColor: colors.greenAccent[700] },
              borderRadius: '8px',
              px: 3,
              minWidth: '96px',
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: colors.primary[400],
            backgroundImage: 'none',
            borderRadius: '16px',
            border: `1px solid ${colors.redAccent[700]}`,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.1rem', color: colors.redAccent[400], pb: 1, px: 3 }}>
          Delete Financial Action
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 1 }}>
          <Typography sx={{ color: colors.grey[200], lineHeight: 1.7 }}>
            Are you sure you want to delete{' '}
            <Box component="span" sx={{ fontWeight: 700, color: colors.grey[100] }}>
              {rowToDelete?.name}
            </Box>
            ? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1.5 }}>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            variant="outlined"
            sx={{
              borderColor: colors.grey[500],
              color: colors.grey[200],
              ':hover': { borderColor: colors.grey[300], backgroundColor: `${colors.grey[700]}33` },
              borderRadius: '8px',
              px: 3,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{
              backgroundColor: colors.redAccent[500],
              color: '#fff',
              fontWeight: 700,
              ':hover': { backgroundColor: colors.redAccent[600] },
              borderRadius: '8px',
              px: 3,
            }}
            onClick={async () => {
              setDeleteConfirmOpen(false);
              const res = await fetch(
                `${process.env.REACT_APP_APILINK}/api/admin/assigned-trips/financial-actions/${rowToDelete.id}`,
                { method: 'DELETE', headers: { token } }
              );
              const data = await res.json();
              setToast(data.success ? 'Deleted' : data.message || 'Delete failed');
              if (data.success) getData();
              setRowToDelete(null);
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast('')}
        message={toast}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </Box>
  );
};

export default AssignedTripDetails;
